import React, { Suspense, lazy, useRef, useState } from 'react';
import {
  SecondaryIconButton,
  SecondaryButton,
  ButtonIcon,
  BrandBadge,
  InfoBadge,
  NeutralInverseBadge,
} from '@hz-design-system/web-ui';
import { Trans, useI18nContext } from '@ecg-marktplaats/js-react-i18n';

import useCarousel from './hooks/useCarousel';
import NoImage from './NoImage';
import Thumbnails from './Thumbnails';
import HeroImage from './HeroImage';
import FavouriteButton from '../Listing/FavouriteButton';
import { trackGAEvent } from '../../../../utils/gaTracking';
import { GA_TRACK_EVENTS_ACTIONS } from '../../../../utils/constants';

import { TListingGallery } from 'types/TListing';

import 'react-responsive-carousel/lib/styles/carousel.min.css'; // requires a loader
import Classes from './Gallery.module.scss';

const CarouselDialog = lazy(() => import(/* webpackChunkName: "CarouselDialog" */ './CarouselDialog'));
const VideoModal = lazy(() => import(/* webpackChunkName: "VideoModal" */ './VideoModal'));

type TGalleryProps = {
  gallery: TListingGallery;
  title: string;
  itemId: string;
  showMicroTipText: boolean;
  showUrgencyText: boolean;
  withFavoriteButton?: boolean;
  favoritedCount: number;
  isSaved: boolean;
};

const Gallery = ({
  gallery: {
    imageUrls = [],
    microTipText,
    media: { video, imageSizes },
    alt,
  },
  title,
  itemId,
  showMicroTipText,
  showUrgencyText,
  withFavoriteButton,
  favoritedCount,
  isSaved,
}: TGalleryProps) => {
  const { t } = useI18nContext();
  const galleryContainerElement = useRef<HTMLHeadingElement>(null);
  const hasImage = Boolean(imageUrls.length);
  const [showCarouselDialog, setShowCarouselDialog] = useState(false);
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [imageIndex, setImageIndex] = useState(1);

  const toggleCarouselModal = () => {
    trackGAEvent(
      showCarouselDialog ? GA_TRACK_EVENTS_ACTIONS.CLOSE_PHOTO_GALLERY : GA_TRACK_EVENTS_ACTIONS.OPEN_PHOTO_GALLERY,
    );
    setShowCarouselDialog(!showCarouselDialog);
  };

  const toggleVideoModal = () => {
    trackGAEvent(
      showVideoModal ? GA_TRACK_EVENTS_ACTIONS.CLOSE_VIDEO_GALLERY : GA_TRACK_EVENTS_ACTIONS.OPEN_VIDEO_GALLERY,
    );
    setShowVideoModal(!showVideoModal);
  };

  const changeCarouselIndex = (index) => {
    setImageIndex(index + 1);
  };

  const { ModalCarousel, GalleryCarousel, showCarousel } = useCarousel({
    imageUrls,
    imageSizes,
    title,
    alt,
    changeCarouselIndex,
    toggleCarouselModal,
  });

  return (
    <>
      <div ref={galleryContainerElement} className={Classes.root}>
        <div className={Classes.container}>
          {showMicroTipText && microTipText && <BrandBadge className={Classes.badge}>{microTipText}</BrandBadge>}
          {showUrgencyText && microTipText && <InfoBadge className={Classes.badge}>{microTipText}</InfoBadge>}
          {!hasImage && <NoImage />}
          {hasImage && !showCarousel && (
            <HeroImage imageUrl={imageUrls[0]} imageSizes={imageSizes} alt={alt} onClickHandler={toggleCarouselModal} />
          )}
          {showCarousel && (
            <>
              <GalleryCarousel index={imageIndex - 1} />
              <NeutralInverseBadge className={Classes.carouselIndexContainer}>
                {imageIndex}/{imageUrls.length}
              </NeutralInverseBadge>
            </>
          )}
          <div className={Classes.actions}>
            {video && (
              <SecondaryButton onClick={toggleVideoModal}>
                <ButtonIcon name="player-play" isStartIcon={false} />
                {t('gallery.videoLabel')}
              </SecondaryButton>
            )}
            {hasImage && (
              <SecondaryIconButton
                className={Classes.zoom}
                name="zoom-in"
                onClick={toggleCarouselModal}
                ariaLabel={t('gallery.zoomLabel')}
              />
            )}
            {withFavoriteButton && (
              <FavouriteButton isSaved={isSaved} itemId={itemId} favoritedCount={favoritedCount} />
            )}
          </div>
        </div>

        {imageUrls.length > 1 && (
          <Thumbnails
            onChange={changeCarouselIndex}
            imageUrls={imageUrls}
            imageSizes={imageSizes}
            activeImageIndex={imageIndex - 1}
          />
        )}
      </div>
      {showCarouselDialog && ModalCarousel && (
        <Suspense>
          <CarouselDialog toggleCarouselModal={toggleCarouselModal}>
            <div className={Classes.modalCarousel}>
              <ModalCarousel index={imageIndex - 1} />
              <NeutralInverseBadge className={Classes.carouselIndexContainerForModal}>
                {imageUrls.length > 1 ? (
                  <span>
                    {imageIndex}/{imageUrls.length}
                  </span>
                ) : (
                  <Trans i18nKey="gallery.image" count={imageIndex} tagName="span" />
                )}
              </NeutralInverseBadge>
            </div>
            {imageUrls.length > 1 && (
              <div className={Classes.dialogThumbnails}>
                <Thumbnails
                  onChange={changeCarouselIndex}
                  imageUrls={imageUrls}
                  imageSizes={imageSizes}
                  activeImageIndex={imageIndex - 1}
                  loadAll
                />
              </div>
            )}
          </CarouselDialog>
        </Suspense>
      )}
      {showVideoModal && video && (
        <Suspense>
          <VideoModal video={video} toggleVideoModal={toggleVideoModal} title={t('gallery.video-title')} />
        </Suspense>
      )}
    </>
  );
};

export default Gallery;
